import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, RefreshCw, ArrowUpRight, ArrowDownLeft, ArrowDown } from 'lucide-react';
import { apiGet } from '../api/client.js';
import { useAuthStore } from '../store/auth.store.js';
import { useClientStore } from '../store/client.store.js';

/* ── utilidades ─────────────────────────────────── */
function fmt(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  return d.toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'medium' });
}

function statusColor(s) {
  if (!s) return 'text-slate-400';
  if (['processed', 'accredited', 'approved'].includes(s)) return 'text-emerald-400';
  if (['canceled', 'cancelled', 'rejected'].includes(s)) return 'text-red-400';
  if (['created', 'at_terminal', 'processing'].includes(s)) return 'text-amber-400';
  return 'text-slate-300';
}

/* ── JSON expandible ─────────────────────────────── */
function JsonViewer({ data, title, dir = 'out' }) {
  const [open, setOpen] = useState(false);

  // dir='ideafix' → siempre visible aunque data sea null (muestra placeholder)
  const isIdeafix = dir === 'ideafix';
  if (!data && !isIdeafix) return null;

  const obj = data
    ? (typeof data === 'string' ? (() => { try { return JSON.parse(data); } catch { return data; } })() : data)
    : null;

  const Icon  = dir === 'out' ? ArrowUpRight : dir === 'ideafix' ? ArrowDown : ArrowDownLeft;
  const color = dir === 'out' ? 'text-blue-400' : dir === 'ideafix' ? 'text-amber-400' : 'text-emerald-400';

  return (
    <div className="mt-1">
      <button
        onClick={() => { if (obj) setOpen(o => !o); }}
        className={`flex items-center gap-1.5 text-xs font-medium ${color} ${obj ? 'hover:opacity-80 cursor-pointer' : 'opacity-50 cursor-default'} transition-opacity`}
      >
        <Icon className="w-3 h-3" />
        {title}
        {obj
          ? (open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />)
          : <span className="text-slate-500 font-normal ml-1">(no disponible en órdenes anteriores)</span>
        }
      </button>
      {open && obj && (
        <pre className="mt-1.5 p-3 bg-black/40 rounded border border-border text-xs text-slate-300 overflow-x-auto max-h-80 font-mono leading-relaxed">
          {JSON.stringify(obj, null, 2)}
        </pre>
      )}
    </div>
  );
}

/* ── Fila de orden ───────────────────────────────── */
function OrderRow({ o }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-border rounded-md bg-bg-elevated overflow-hidden">
      <button
        onClick={() => setOpen(x => !x)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-bg-strong/30 transition-colors"
      >
        {open ? <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />}
        <div className="flex-1 min-w-0 grid grid-cols-[1fr_auto_auto_auto] gap-3 items-center">
          <div className="min-w-0">
            <div className="font-mono text-xs text-slate-300 truncate">{o.order_id}</div>
            <div className="text-xs text-slate-500 truncate">{o.external_reference} · {o.pos_number}</div>
          </div>
          <div className="text-right">
            <div className="text-sm font-semibold text-slate-200">${parseFloat(o.amount || 0).toFixed(2)}</div>
            <div className="text-xs text-slate-500">{o.payment_method}</div>
          </div>
          <div className="text-right">
            <span className={`text-xs font-medium ${statusColor(o.status)}`}>{o.status}</span>
            {o.payment_status_detail && (
              <div className={`text-xs ${statusColor(o.payment_status_detail)}`}>{o.payment_status_detail}</div>
            )}
          </div>
          <div className="text-xs text-slate-500 text-right whitespace-nowrap">
            {o.client_code && <div className="text-slate-400 font-medium">{o.client_code}</div>}
            {fmt(o.created_date)}
          </div>
        </div>
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-2 border-t border-border pt-3">
          <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs mb-2">
            {[
              ['Order ID',       o.order_id],
              ['Ext. Ref.',      o.external_reference],
              ['POS',            o.pos_number],
              ['Terminal MP',    o.mp_terminal_id],
              ['Status',         o.status],
              ['Payment status', `${o.payment_status} / ${o.payment_status_detail || '—'}`],
              ['Monto',          `$${parseFloat(o.amount||0).toFixed(2)}`],
              ['Método',         o.payment_method],
              ['Creado',         fmt(o.created_date)],
              ['Actualizado',    fmt(o.updated_date)],
            ].map(([k, v]) => v && (
              <div key={k} className="flex gap-1">
                <span className="text-slate-500 shrink-0">{k}:</span>
                <span className="font-mono text-slate-300 break-all">{v}</span>
              </div>
            ))}
          </div>
          <JsonViewer data={o.incoming_request} title="Request recibido de Ideafix" dir="ideafix" />
          <JsonViewer data={o.mp_request}      title="Request enviado a MP"        dir="out" />
          <JsonViewer data={o.mp_response}     title="Response recibido de MP"     dir="in"  />
        </div>
      )}
    </div>
  );
}

/* ── Fila de webhook ─────────────────────────────── */
function WebhookRow({ w }) {
  const [open, setOpen] = useState(false);
  const ok = w.processed;
  return (
    <div className="border border-border rounded-md bg-bg-elevated overflow-hidden">
      <button
        onClick={() => setOpen(x => !x)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-bg-strong/30 transition-colors"
      >
        {open ? <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />}
        <div className="flex-1 min-w-0 grid grid-cols-[auto_1fr_auto_auto] gap-3 items-center">
          <div className={`w-2 h-2 rounded-full shrink-0 ${ok ? 'bg-emerald-400' : w.processed ? 'bg-red-400' : 'bg-amber-400'}`} />
          <div className="min-w-0">
            <div className="text-xs font-medium text-slate-300">{w.action || w.type || '—'}</div>
            {w.order_id && <div className="font-mono text-xs text-slate-500 truncate">{w.order_id}</div>}
          </div>
          <div className="text-xs text-right">
            {w.client_code && <div className="text-slate-400 font-medium">{w.client_code}</div>}
            <span className={`${ok ? 'text-emerald-400' : 'text-amber-400'}`}>
              {ok ? 'Procesado' : 'Pendiente'}
            </span>
          </div>
          <div className="text-xs text-slate-500 text-right whitespace-nowrap">{fmt(w.received_at)}</div>
        </div>
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-2 border-t border-border pt-3">
          <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs mb-2">
            {[
              ['ID',           w.id],
              ['Tipo',         w.type],
              ['Acción',       w.action],
              ['Order ID',     w.order_id],
              ['Cliente',      w.client_code],
              ['Firma válida', w.signature_valid == null ? 'No verificada' : w.signature_valid ? 'Sí' : 'No'],
              ['Procesado',    w.processed ? `Sí — ${fmt(w.processed_at)}` : 'No'],
              ['Reenviado',    w.forwarded ? 'Sí' : 'No'],
            ].map(([k, v]) => v != null && (
              <div key={k} className="flex gap-1">
                <span className="text-slate-500 shrink-0">{k}:</span>
                <span className={`font-mono break-all ${k === 'Error' ? 'text-red-400' : 'text-slate-300'}`}>{String(v)}</span>
              </div>
            ))}
          </div>
          <JsonViewer data={w.payload}          title="Payload recibido de MP"      dir="in"  />
          <JsonViewer data={w.forward_response} title="Respuesta al reenvío"        dir="out" />
        </div>
      )}
    </div>
  );
}

/* ── Fila de llamada a MP API ────────────────────── */
function MpApiRow({ r }) {
  const [open, setOpen] = useState(false);
  const ok  = r.response_status && r.response_status < 400;
  const err = !!r.error;
  const statusColor = ok ? 'text-emerald-400' : err ? 'text-red-400' : 'text-amber-400';
  const dotColor    = ok ? 'bg-emerald-400'   : err ? 'bg-red-400'   : 'bg-amber-400';

  return (
    <div className="border border-border rounded-md bg-bg-elevated overflow-hidden">
      <button
        onClick={() => setOpen(x => !x)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-bg-strong/30 transition-colors"
      >
        {open ? <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />}
        <div className="flex-1 min-w-0 grid grid-cols-[auto_auto_1fr_auto_auto] gap-3 items-center">
          <div className={`w-2 h-2 rounded-full shrink-0 ${dotColor}`} />
          <span className="text-xs font-mono font-bold text-slate-400 shrink-0">{r.method}</span>
          <div className="min-w-0">
            <div className="font-mono text-xs text-slate-300 truncate">{r.path}</div>
            {r.client_code && <div className="text-xs text-slate-500">{r.client_code} · {r.service}</div>}
          </div>
          <span className={`text-xs font-medium shrink-0 ${statusColor}`}>
            {r.response_status ?? '—'}
            {r.duration_ms != null && <span className="text-slate-500 font-normal ml-1">{r.duration_ms}ms</span>}
          </span>
          <div className="text-xs text-slate-500 text-right whitespace-nowrap shrink-0">{fmt(r.created_at)}</div>
        </div>
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-2 border-t border-border pt-3">
          {r.error && (
            <p className="text-xs text-red-400 font-mono bg-red-900/20 px-2 py-1 rounded">{r.error}</p>
          )}
          <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs mb-2">
            {[
              ['Servicio',  r.service],
              ['Cliente',   r.client_code],
              ['Token',     r.access_token_hint],
              ['Método',    r.method],
              ['Path',      r.path],
              ['Status',    r.response_status],
              ['Duración',  r.duration_ms != null ? `${r.duration_ms}ms` : null],
              ['Fecha',     fmt(r.created_at)],
            ].map(([k, v]) => v != null && (
              <div key={k} className="flex gap-1">
                <span className="text-slate-500 shrink-0">{k}:</span>
                <span className="font-mono text-slate-300 break-all">{String(v)}</span>
              </div>
            ))}
          </div>
          <JsonViewer data={r.incoming_request} title="Request recibido de Ideafix" dir="ideafix" />
          <JsonViewer data={r.request_body}   title="Request enviado a MP"        dir="out"     />
          <JsonViewer data={r.response_body}  title="Response recibido de MP"     dir="in"      />
        </div>
      )}
    </div>
  );
}

const ORDER_STATUS_OPTS   = ['created', 'processed', 'canceled', 'open', 'at_terminal', 'processing'];
const PAYMENT_METHOD_OPTS = ['credit_card', 'debit_card', 'qr', 'cash', 'efectivo'];
const LIMIT_OPTS          = [50, 100, 200];
const HTTP_METHOD_OPTS    = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

/* ── Página principal ────────────────────────────── */
export default function Logs() {
  const [tab, setTab] = useState('mp-api');
  const user = useAuthStore(s => s.user);
  const { selectedClientId } = useClientStore();

  // Filtros compartidos
  const [limit,         setLimit]         = useState(100);
  // Filtros solo para órdenes
  const [orderStatus,   setOrderStatus]   = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  // Filtros solo para mp-api
  const [mpApiMethod,   setMpApiMethod]   = useState('');

  const clientParam = user?.role === 'superadmin' && selectedClientId
    ? `clientId=${selectedClientId}&`
    : '';

  const orderParams = [
    clientParam,
    `limit=${limit}`,
    orderStatus   ? `&status=${orderStatus}`              : '',
    paymentMethod ? `&paymentMethod=${paymentMethod}`     : '',
  ].join('');

  const ordersQ = useQuery({
    queryKey: ['logs-orders', selectedClientId, limit, orderStatus, paymentMethod],
    queryFn: () => apiGet(`/logs/orders?${orderParams}`),
    enabled: tab === 'orders',
    refetchInterval: 30_000,
  });

  const webhooksQ = useQuery({
    queryKey: ['logs-webhooks', selectedClientId, limit],
    queryFn: () => apiGet(`/logs/webhooks?${clientParam}limit=${limit}`),
    enabled: tab === 'webhooks',
    refetchInterval: 30_000,
  });

  const mpApiParams = [
    clientParam,
    `limit=${limit}`,
    mpApiMethod ? `&method=${mpApiMethod}` : '',
  ].join('');

  const mpApiQ = useQuery({
    queryKey: ['logs-mp-api', selectedClientId, limit, mpApiMethod],
    queryFn: () => apiGet(`/logs/mp-api?${mpApiParams}`),
    enabled: tab === 'mp-api',
    refetchInterval: 20_000,
  });

  const activeQ = tab === 'orders' ? ordersQ : tab === 'webhooks' ? webhooksQ : mpApiQ;
  const items   = activeQ.data || [];

  const TABS = [
    { id: 'mp-api',   label: 'Llamadas a MP',       q: mpApiQ    },
    { id: 'orders',   label: 'Órdenes (detalle)',    q: ordersQ   },
    { id: 'webhooks', label: 'Webhooks recibidos',   q: webhooksQ },
  ];

  return (
    <div className="p-6 space-y-5 max-w-5xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-100">Logs de integración</h1>
          <p className="text-sm text-slate-400 mt-0.5">
            Todos los requests y responses enviados/recibidos desde MercadoPago
          </p>
        </div>
        <button
          onClick={() => activeQ.refetch()}
          disabled={activeQ.isFetching}
          className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-200 transition-colors px-3 py-1.5 rounded border border-border hover:border-border-strong"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${activeQ.isFetching ? 'animate-spin' : ''}`} />
          Actualizar
        </button>
      </div>

      {/* Tabs + controles */}
      <div className="flex items-end justify-between border-b border-border gap-4">
        <div className="flex">
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
                tab === t.id
                  ? 'border-accent text-accent'
                  : 'border-transparent text-slate-400 hover:text-slate-200'
              }`}
            >
              {t.label}{t.q.data ? ` (${t.q.data.length})` : ''}
            </button>
          ))}
        </div>

        {/* Selector de cantidad por página — siempre visible */}
        <div className="flex items-center gap-1.5 pb-2 text-xs text-slate-400 shrink-0">
          <span>Mostrar</span>
          {LIMIT_OPTS.map(n => (
            <button
              key={n}
              onClick={() => setLimit(n)}
              className={`px-2 py-0.5 rounded transition-colors border ${
                limit === n
                  ? 'border-accent/50 bg-accent/10 text-accent font-semibold'
                  : 'border-border hover:border-border-strong text-slate-400'
              }`}
            >
              {n}
            </button>
          ))}
        </div>
      </div>

      {/* Filtros para órdenes */}
      {tab === 'orders' && (
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="label text-xs">status</label>
            <select
              className="input-base text-sm"
              value={orderStatus}
              onChange={e => setOrderStatus(e.target.value)}
            >
              <option value="">— todos —</option>
              {ORDER_STATUS_OPTS.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className="label text-xs">Tipo de petición</label>
            <select
              className="input-base text-sm"
              value={paymentMethod}
              onChange={e => setPaymentMethod(e.target.value)}
            >
              <option value="">— todos —</option>
              {PAYMENT_METHOD_OPTS.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          {(orderStatus || paymentMethod) && (
            <button
              onClick={() => { setOrderStatus(''); setPaymentMethod(''); }}
              className="text-xs text-slate-400 hover:text-slate-200 px-2 py-1.5 border border-border rounded hover:border-border-strong transition-colors"
            >
              Limpiar
            </button>
          )}
        </div>
      )}

      {/* Filtros para llamadas a MP */}
      {tab === 'mp-api' && (
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="label text-xs">Método HTTP</label>
            <div className="flex gap-1.5">
              <button
                onClick={() => setMpApiMethod('')}
                className={`px-2.5 py-1 text-xs rounded border transition-colors ${
                  !mpApiMethod
                    ? 'border-accent/50 bg-accent/10 text-accent font-semibold'
                    : 'border-border hover:border-border-strong text-slate-400'
                }`}
              >
                Todos
              </button>
              {HTTP_METHOD_OPTS.map(m => (
                <button
                  key={m}
                  onClick={() => setMpApiMethod(mpApiMethod === m ? '' : m)}
                  className={`px-2.5 py-1 text-xs rounded border transition-colors font-mono ${
                    mpApiMethod === m
                      ? 'border-accent/50 bg-accent/10 text-accent font-semibold'
                      : 'border-border hover:border-border-strong text-slate-400'
                  }`}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Contenido */}
      {activeQ.isLoading ? (
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-14 bg-bg-elevated rounded-md border border-border animate-pulse" />
          ))}
        </div>
      ) : activeQ.isError ? (
        <p className="text-sm text-red-400">Error cargando logs: {activeQ.error?.message}</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-slate-500 py-8 text-center">No hay registros todavía.</p>
      ) : (
        <div className="space-y-1.5">
          {tab === 'mp-api'
            ? items.map(r => <MpApiRow key={r.id} r={r} />)
            : tab === 'orders'
            ? items.map(o => <OrderRow key={o.order_id} o={o} />)
            : items.map(w => <WebhookRow key={w.id} w={w} />)
          }
        </div>
      )}
    </div>
  );
}
